import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";

import { WPTypography } from "../../packages/WPTypography";
import CustomTooltip from "../../common/styles/components/tooltip/CustomTooltip";
import {
  WPHeadingSize,
  WPHeadingSizeFontMapping,
  WPHeadingTypes,
} from "./WPHeading.constant";

const WPHeading = ({
  id,
  size,
  type,
  text,
  children,
  color,
  secondary,
  className,
  tooltip,
  wrapContent,
  tooltipPlacement = "top",
  renderHtml,
  fontNoto,
  showTooltipAlways = false,
  needResizeObserver = false,
}) => {
  const nodeRef = useRef(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  const checkOverflow = () => {
    if (nodeRef.current) {
      const isOverFlow = nodeRef.current.scrollWidth > nodeRef.current.clientWidth;
      setIsOverflowing(isOverFlow || showTooltipAlways);
    }
  };

  useEffect(() => {
    if (nodeRef?.current) {
      checkOverflow();

      if (needResizeObserver) {
        const resizeObserver = new ResizeObserver(checkOverflow);
        resizeObserver.observe(nodeRef.current);

        return () => {
          if (nodeRef.current) {
            resizeObserver.unobserve(nodeRef.current);
          }
        };
      }
    }
  }, [children, text, nodeRef?.current]);

  return (
    <CustomTooltip
      text={isOverflowing ? tooltip : ""}
      placement={tooltipPlacement}
    >
      <div className="max-width-100">
        <WPTypography
          id={id}
          ref={nodeRef}
          variant={size}
          text={text}
          color={color || WPHeadingTypes[type]}
          asElement={WPHeadingSize[size]}
          className={`${className} ${WPHeadingSizeFontMapping[size]} ${
            wrapContent ? "text-overflow-ellipsis" : ""
          }`}
          fontFamily={fontNoto ? "NotoSans" : "Diodrum"}
          renderHtml={renderHtml}
          fontWeight={secondary ? "400" : "600"}
        >
          {children}
        </WPTypography>
      </div>
    </CustomTooltip>
  );
};

WPHeading.propTypes = {
  id: PropTypes.string,
  size: PropTypes.oneOf([
    "xxSmall",
    "xSmall",
    "small",
    "medium",
    "large",
    "xLarge",
    "xxLarge",
  ]),
  type: PropTypes.oneOf(["default", "primary"]),
  text: PropTypes.string,
  children: PropTypes.node,
  color: PropTypes.string,
  secondary: PropTypes.bool,
  className: PropTypes.string,
  tooltip: PropTypes.string,
  wrapContent: PropTypes.bool,
  renderHtml: PropTypes.string,
  fontNoto: PropTypes.bool,
};

WPHeading.defaultProps = {
  id: null,
  size: "medium",
  type: "primary",
  text: "",
  children: null,
  color: null,
  secondary: false,
  className: "",
  tooltip: "",
  wrapContent: true,
  renderHtml: null,
  fontNoto: false,
};

export default WPHeading;
